<script setup lang="ts">
type PublicTrainingRow = {
  id: string
  title: string
  location: string
  sessions: Array<{ date: string, startTime: string, endTime: string }>
  price: number
  remainingPlaces: number
  isLastPlaces: boolean
}

const props = withDefaults(defineProps<{ limit?: number }>(), { limit: 3 })

// Chargé côté navigateur : les pages qui l'affichent sont pré-rendues, les places doivent rester à jour.
const { data, status } = useFetch<PublicTrainingRow[]>('/api/training/public', {
  key: 'public-trainings',
  server: false,
  lazy: true,
  default: () => [],
})

// « Chargé » devient vrai une fois la réponse reçue (succès ou erreur). Avant, le squelette est rendu côté serveur
// comme côté navigateur : le HTML prérendu et l'hydratation produisent le même arbre (plus d'écart d'hydratation).
const loaded = ref(false)
watch(status, (value) => {
  if (value === 'success' || value === 'error') {
    loaded.value = true
  }
}, { immediate: true })

const trainings = computed(() => (data.value || []).filter(t => t.sessions.length > 0).slice(0, props.limit))

const formatDate = (date: string) => new Date(date).toLocaleDateString('fr-BE', { weekday: 'short', day: 'numeric', month: 'long' })
const formatPrice = (price: number) => (Number.isFinite(price) && price > 0
  ? new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(price)
  : '')
</script>

<template>
  <div v-if="!loaded || trainings.length">
    <div
      v-if="!loaded"
      class="grid gap-4 md:grid-cols-3"
    >
      <USkeleton
        v-for="n in props.limit"
        :key="n"
        class="h-44 rounded-[var(--radius-tile)]"
      />
    </div>
    <div
      v-else
      class="grid gap-4 md:grid-cols-3"
    >
      <NuxtLink
        v-for="training in trainings"
        :key="training.id"
        to="/formations/formations-grand-public#prochaines-formations"
        class="group flex flex-col rounded-[var(--radius-tile)] border border-[var(--line-soft)] bg-[var(--surface-1)] p-5 transition-colors hover:border-primary-400 dark:hover:border-primary-500"
      >
        <div class="flex items-start justify-between gap-3">
          <p class="font-bold text-[var(--ink-900)] group-hover:text-primary-600 dark:group-hover:text-primary-400">
            {{ training.title }}
          </p>
          <UBadge
            :color="training.remainingPlaces === 0 ? 'error' : training.isLastPlaces ? 'warning' : 'success'"
            variant="subtle"
            class="shrink-0"
          >
            {{ training.remainingPlaces === 0 ? 'Complet' : `${training.remainingPlaces} place${training.remainingPlaces > 1 ? 's' : ''}` }}
          </UBadge>
        </div>
        <ul class="mt-3 space-y-1 text-sm text-[var(--ink-700)]">
          <li
            v-for="session in training.sessions"
            :key="`${session.date}-${session.startTime}`"
            class="flex items-center gap-2"
          >
            <UIcon
              name="i-heroicons-calendar-days"
              class="h-4 w-4 shrink-0 text-brand-blue-600"
            />
            {{ formatDate(session.date) }} · {{ session.startTime }}-{{ session.endTime }}
          </li>
          <li class="flex items-center gap-2">
            <UIcon
              name="i-heroicons-map-pin"
              class="h-4 w-4 shrink-0 text-brand-blue-600"
            />
            {{ training.location }}
          </li>
        </ul>
        <div class="mt-auto flex items-center justify-between pt-4">
          <span class="text-lg font-bold text-[var(--ink-900)]">{{ formatPrice(training.price) }}</span>
          <span class="text-sm font-semibold text-primary-600 dark:text-primary-400">Réserver →</span>
        </div>
      </NuxtLink>
    </div>
  </div>
</template>
